import type {
  TableColumnsResponseApi,
  TableRowsResponseApi,
} from '../types/config-table.api.types';
import type {
  ConfigTableColumnMeta,
  ConfigTableFacets,
  ConfigTablePage,
} from '../types/config-table.types';

/**
 * Envelope mappers shared by every config-table listing. Per-table row mapping
 * lives in each table's own mapper and arrives here as `mapRow`.
 */

/**
 * Maps the paginated envelope to a domain page.
 *
 * Reads `total`/`page`/`size` from the server's metadata rather than inferring
 * them from `data.length` — the last page is short, and the paginator needs the
 * true total to render page numbers.
 */
export function toConfigTablePage<TRowApi, TRow>(
  api: TableRowsResponseApi<TRowApi>,
  mapRow: (row: TRowApi) => TRow,
): ConfigTablePage<TRow> {
  return {
    rows: api.data.map(mapRow),
    total: api.pagination.total,
    page: api.pagination.page,
    size: api.pagination.size,
    totalPages: api.pagination.total_pages,
  };
}

/**
 * Maps `/tables/columns` to the domain column list.
 *
 * A column whose `values` is `null` (past the 500-distinct cap) becomes an empty
 * list rather than staying null, so a filter can distinguish "this column has no
 * enumerable list, render free text" from "the whole call failed". Values are
 * stringified because the endpoint is generic and types them `unknown[]` — a
 * faceted integer column such as `gpc_categories.depth` yields numbers.
 */
export function toConfigTableColumns(api: TableColumnsResponseApi): ConfigTableColumnMeta[] {
  return api.columns.map((column) => ({
    name: column.name,
    label: column.label,
    type: column.type,
    filterable: column.filterable,
    sortable: column.sortable,
    searchable: column.searchable,
    operators: column.operators,
    values: column.values ? column.values.map((value) => String(value)) : [],
    valuesTruncated: column.values_truncated,
  }));
}

/**
 * Reduces the domain column list to `column name → facet values`.
 *
 * A pure projection rather than a second parse of the response: the filter bars
 * only ever want the values, while the AI-filter chips need the labels too, and
 * both read one cached `/tables/columns` result. Used as TanStack Query's `select`
 * in `useConfigTableFacets`, so no call site had to change when the cached shape
 * widened.
 */
export function toConfigTableFacets(columns: ConfigTableColumnMeta[]): ConfigTableFacets {
  const facets: ConfigTableFacets = {};
  for (const column of columns) {
    facets[column.name] = column.values;
  }
  return facets;
}
